import React from 'react';
import { call, select, put, cancelled, race, take, delay } from 'redux-saga/effects';
import * as Sentry from '@sentry/browser';
import { Set } from 'immutable';
import Api, { doLogout } from '@/domain/api';
import { documentGetTagsAction, documentSelector, documentDocumentIdSelector } from '@/domain/documents';
import { get as _get } from 'lodash';
import { FormattedMessage } from 'react-intl';

import * as actions from '@/domain/journal/actions';
import * as adapters from '@/domain/journal/adapters';
import * as selectors from '@/domain/journal/selectors';
import * as ApprovalActions from '@/domain/approvals/actions';
import { textractKeys } from '@/domain/textract/queries';
import { currentCompanyIdSelector } from '@/domain/router/routerLeafSelectors';
import { queryClient } from '@/services/query';
import { ApprovalsStoreAdapter } from '@/domain/approvals';
import { listToOptions, swapJEGridLineColumnsData, JournalSettingOrderFactory } from '@/domain/journal/helper';
import { ToastEventHandler } from '@/components/Tables/toast';
import { documentGet } from '@/domain/documents/documentsActions';
import { documentAdapterValidated } from '@/domain/documents/helpers';
import indexedDb from '@/indexedDb';

import { QUERY_ALL_PAGES } from '@/components/Tables/Cells/ListCell/PaginationAutocomplete';
import { jeGridLineColumnsDictionarySelector } from '@/domain/journal/selectors';
import {
  getJEntryIndexedDBTableStoreKey,
  migrateJETablesFromLetterToID,
  migrateJETableDisabledColumnsToTableColumnsVisibility,
} from '@/domain/journal/sagas.migrations';
import { yieldSuccessNotification, yieldWarningNotification } from '@/lib/toasts';
import { documentForceSyncErpCompletedAction } from '@/domain/ws/actions';

const WS_EVENT_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

export { actions };

function getJEFactory(action) {
  return function* ensureJE(
    documentID,
    needUpdateDocument = true,
    reindexCache = false,
    page = 1,
    pageSize = 30,
    readonly,
    resolve,
    reject,
    skipPolicies,
  ) {
    yield put({
      type: action.request,
    });

    try {
      const { data } = yield call(Api.getJournalEntry, {
        params: {
          documentID,
          reindexCache,
          page,
          pageSize,
          readonly,
          skip_policies: skipPolicies,
        },
      });

      const payload = adapters.JEAdapter(data);

      yield put({
        type: action.success,
        payload,
      });

      // updating document with data coming from je response
      // used to update tags after je was retrieved as tags might change
      // during je is retrieved

      // we do not update the document when binding columns, as this causes problems for xls
      // documents (the binding is done in pdf view, and after rerendering we get the document in xls view,
      // and the binding data is lost until we refresh the page)
      if (needUpdateDocument) {
        try {
          yield put({
            type: documentGet.success,
            payload: documentAdapterValidated(data.document),
          });
        } catch (err) {
          Sentry.captureMessage('Failed tp update document from JE response');
          console.error('Failed tp update document from JE response', err);
        }
      }

      // This was transfered to ComponentDidMount
      // as shown tosts will be removed by ComponentWillUnmount
      // when navigating with NextDocument
      // ToastEventHandler.onResponce(data);

      if (typeof resolve === 'function') resolve();

      // Return the raw response so callers that need post-fetch document data
      // (e.g. force-sync refreshing tags) can read it. Existing callers ignore
      // the return value.
      return data;
    } catch (err) {
      yield put({
        type: action.failure,
        err,
      });
      if (typeof reject === 'function') reject();
      yield doLogout(action, err);
    }
  };
}

export const ensureJounalEntry = getJEFactory(actions.getJournalEntryAction);

export function updateJEFactory(inAdapter) {
  return function* ensureUpdateJE({ payload }) {
    try {
      const priorJeInvoiceType = yield select(selectors.jeInvoiceTypeSelector);
      const priorJeCurrency = yield select(selectors.jeCurrencySelector);
      const update = inAdapter(payload);

      // eslint-disable-next-line
      const { data } = yield call(Api.updateJournalEntry, {
        data: {
          update: JSON.stringify(update),
          documentID: payload.documentID,
          ...payload.pageParams,
          ...(payload.hotkey ? { hotkey: payload.hotkey } : {}),
        },
      });
      ToastEventHandler.onResponce(adapters.messagesAdapter(data.messages));

      const jePayload = adapters.JEAdapter(data);
      const { document } = data;

      yield put({
        type: actions.updateJournalEntryAction.success,
        payload: jePayload,
      });

      // Transaction type or currency change must produce a new list of fields
      // to bind to — invalidate the textract field-binding catalogue so it is
      // fetched again.
      //
      // Both `jeInvoiceType` and `jeCurrency` are top-level fields on the JE
      // API response, populated by `JEAdapter` (via the `...data` rest spread
      // into `JournalFactory`) and merged into Redux state only on
      // `updateJournalEntryAction.success`. The optimistic
      // `updateJournalEntryAction.type` reducer only mutates
      // `entry[_cell].cellSet` — it never touches top-level state fields — so
      // a before/after compare of these selectors across the API call is
      // reliable for direct cell edits, vendor-cascade auto-fills, and any
      // other mutation path. Default `refetchType: 'active'` is correct: the
      // two-way-match layer observes the catalogue for as long as the view is on.
      const newJeInvoiceType = yield select(selectors.jeInvoiceTypeSelector);
      const newJeCurrency = yield select(selectors.jeCurrencySelector);
      if (newJeInvoiceType !== priorJeInvoiceType || newJeCurrency !== priorJeCurrency) {
        yield call([queryClient, 'invalidateQueries'], {
          queryKey: textractKeys.fieldsMapping(yield select(currentCompanyIdSelector), payload.documentID),
        });
      }

      if (typeof payload.resolve === 'function') {
        payload.resolve();
      }
      yield put({
        type: documentGetTagsAction.success,
        payload: new Set(document.tags),
      });

      yield put({
        type: ApprovalActions.updateApprovalAction.success,
        payload: ApprovalsStoreAdapter(document.approvals),
      });
    } catch (err) {
      if (typeof payload.reject === 'function') payload.reject();
      yield doLogout(actions.updateJournalEntryAction, err);
    } finally {
      /** sagas may cancelled because wee use takeLatest */
      if (yield cancelled()) {
        if (typeof payload.reject === 'function') payload.reject();
      }
    }
  };
}

export function* ensureUpdateJEFromInsights({ payload }) {
  try {
    const { data } = yield call(Api.updateJournalEntryFromInsights, {
      data: {
        ...payload,
      },
    });

    const jePayload = adapters.JEAdapter(data);

    yield put({
      type: actions.updateJEFromInsightsAction.success,
      payload: jePayload,
    });

    yieldSuccessNotification(
      <FormattedMessage
        id="document.insights.toast.copyDataSuccess"
        defaultMessage="Document data updated successfully"
      />,
    );
  } catch (err) {
    yield doLogout(actions.updateJEFromInsightsAction, err);
  }
}

export const ensureUpdateJE = updateJEFactory(({ item }) => item);
// eslint-disable-next-line max-len
export const ensureUpdateEntry = updateJEFactory(({ item, selectedRows }) => adapters.itemAdapter(item, selectedRows));

export function* ensureJounalEntryPage({ payload }) {
  const getJE = getJEFactory(actions.getJournalEntryPageAction);
  const { offset, page, resolve, reject } = payload;
  const { documentID } = yield select(documentSelector);
  try {
    yield call(getJE, documentID, false, page, offset);
    resolve(payload.pageList);
  } catch (err) {
    reject(err);
  }
}

// Triggers async ERP force-sync via the new POST endpoint,
// then waits for the WS event before releasing the loader.
export function* ensureSyncJE(documentID, reindexCache = true, page = 1, pageSize = 30, resolve, reject) {
  try {
    yield call(Api.syncDocumentAsBackgroundTask, { documentID });

    // Wait for WS event confirming ERP sync completion before releasing loader
    const { timeout, wsEvent } = yield race({
      wsEvent: take(documentForceSyncErpCompletedAction.type),
      timeout: delay(WS_EVENT_TIMEOUT_MS),
    });

    if (timeout) {
      ToastEventHandler.onResponceTranslate({
        id: 'document.journalEntry.toast.force_sync_erp_timeout',
        defaultMessage:
          'ERP synchronization is taking longer than expected. Please refresh the page to check the current status',
        type: 'error',
      });
    }

    // Re-fetch JE to get post-sync state. Passing needUpdateDocument=false keeps
    // the XLS field-binding safe (a full documentGet.success from the JE response
    // drops in-progress binding data); instead we refresh only the tags below.
    const data = yield call(ensureJounalEntry, documentID, false, reindexCache, page, pageSize);

    // Reject if WS event reported failure — do this before applying tags so a failed
    // sync never overwrites the open document's tags with a misleading refetch result.
    if (wsEvent?.payload?.status === 'failed') {
      if (typeof reject === 'function') reject();
      return;
    }

    // DA-19720: a successful sync may change document tags (e.g. Priority marks a GR
    // Final), which gate document actions like "Generate Multi-GRV Invoice". Apply the
    // fresh tags from the JE response so those actions update without a page refresh.
    // Guard against a document switch during the (up to 2 min) WS wait — only touch
    // the still-open document's tags.
    const openedDocumentID = yield select(documentDocumentIdSelector);
    if (data?.document?.tags && openedDocumentID === documentID) {
      yield put({
        type: documentGetTagsAction.success,
        payload: new Set(data.document.tags),
      });
    }

    if (typeof resolve === 'function') resolve();
  } catch (err) {
    // 409 Conflict — publish already in progress
    if (err.response && err.response.status === 409) {
      yieldWarningNotification(err.response.data?.message);
      if (typeof resolve === 'function') resolve();
      return;
    }

    if (typeof reject === 'function') reject();
    yield doLogout(actions.getJournalEntryAction, err);
  }
}

export function* ensureGetJournalSettingsPinnedCols() {
  const jeGridLineColumnsDictionary = yield select(jeGridLineColumnsDictionarySelector);
  const storeKey = yield call(getJEntryIndexedDBTableStoreKey);

  if (storeKey) {
    try {
      const data = yield indexedDb.tablePinnedCols.get(storeKey);
      const columns = _get(data, 'pinnedCols', []);
      yield put({
        type: actions.getJournalSettingsPinnedColsAction.success,
        payload: swapJEGridLineColumnsData(columns, jeGridLineColumnsDictionary, true),
      });
    } catch (err) {
      yield doLogout(actions.getJournalSettingsPinnedColsAction, err);
    }
  }
}

export function* ensureUpdateJournalSettingsPinnedCols({ payload }) {
  const jeGridLineColumnsDictionary = yield select(jeGridLineColumnsDictionarySelector);
  const storeKey = yield call(getJEntryIndexedDBTableStoreKey);

  if (storeKey) {
    try {
      yield indexedDb.tablePinnedCols.put({
        tableName: storeKey,
        pinnedCols: swapJEGridLineColumnsData(payload, jeGridLineColumnsDictionary),
      });
      yield put({
        type: actions.updateJournalSettingsPinnedColsAction.success,
        payload,
      });
    } catch (err) {
      yield doLogout(actions.updateJournalSettingsPinnedColsAction, err);
    }
  }
}

export function* ensureGetJournalSettingsOrderedCols() {
  const storeKey = yield call(getJEntryIndexedDBTableStoreKey);
  if (storeKey) {
    try {
      const data = yield indexedDb.tableOrderedCols.get(storeKey);
      const payload = data.orderedCols || {};
      yield put({
        type: actions.getJournalSettingsOrderedColsAction.success,
        payload: JournalSettingOrderFactory(payload),
      });
    } catch (err) {
      yield doLogout(actions.getJournalSettingsOrderedColsAction, err);
    }
  }
}

export function* ensureUpdateJournalSettingsOrderedCols({ payload }) {
  const storeKey = yield call(getJEntryIndexedDBTableStoreKey);
  if (storeKey) {
    try {
      yield indexedDb.tableOrderedCols.put({
        tableName: storeKey,
        orderedCols: payload.toJS(),
      });

      yield put({
        type: actions.updateJournalSettingsOrderedColsAction.success,
        payload,
      });
    } catch (err) {
      yield doLogout(actions.updateJournalSettingsOrderedColsAction, err);
    }
  }
}

// payload - Array of Letters [A, B, D] - array of disabled columns
export function* ensureUpdateJELineColumnsVisibility({ payload }) {
  // lineColumnsDictionary - OrderedMap<columnLetter, columnID>
  const lineColumnsDictionary = yield select(jeGridLineColumnsDictionarySelector);
  const storeKey = yield call(getJEntryIndexedDBTableStoreKey);

  if (storeKey) {
    try {
      // build data for indexedDB
      // Object<columnID, boolean> - if not exist in payload = visible column
      const lineColumnsVisibility = lineColumnsDictionary.reduce(
        (prevObj, columnID, columnLetter) => ({
          ...prevObj,
          [columnID]: !payload.includes(columnLetter),
        }),
        {},
      );

      yield indexedDb.tableJELineColumnsVisibility.put({
        tableName: storeKey,
        lineColumnsVisibility,
      });
      yield put({
        type: actions.updateJELineColumnsVisibilityAction.success,
        payload,
      });
    } catch (err) {
      yield doLogout(actions.updateJELineColumnsVisibilityAction, err);
    }
  }
}

export function* ensureJELineColumnsVisibility() {
  const storeKey = yield call(getJEntryIndexedDBTableStoreKey);
  // OrderedMap<columnLetter, columnID>
  const lineColumnsDictionary = yield select(jeGridLineColumnsDictionarySelector);
  // initialHiddenColumns - immutable List<columnLetter>,
  const initialHiddenColumnLetters = yield select(selectors.initialHiddenColumnsSelector);

  if (storeKey) {
    try {
      const dbLineColumnsVisibility = yield indexedDb.tableJELineColumnsVisibility.get(storeKey);

      if (dbLineColumnsVisibility && dbLineColumnsVisibility.lineColumnsVisibility) {
        // storedColumns - Object<columnID, boolean>
        const storedColumns = dbLineColumnsVisibility.lineColumnsVisibility;
        // filter columns that are hidden in store
        const storedHiddenColumnIDs = Object.entries(storedColumns)
          .filter(([, visible]) => !visible)
          .map(([columnID]) => columnID);
        // in store we save column by id, but for work with JE grid needed letters, so convert from ids to letters
        const storedHiddenColumnLetters = swapJEGridLineColumnsData(storedHiddenColumnIDs, lineColumnsDictionary, true);

        // we check if there is difference between stored columns and columns from b-end
        // for example: in b-end were added or deleted columns, so we need get stored columns and compare with columns from b-end
        // if we find difference between columns state, than we should add/delete column to/from store
        // storedColumns = saved columns in IndexedDB, we always store initialHiddenColumnLetters if they don't exist in indexedDb
        // in redux(journalSettings) we store columns as Array<columnLetter>
        // compare count of columns from store and from b-end, if stored size less than from b-end,
        // that's mean columns were added
        const columnsWereAdded = Object.keys(storedColumns).length < lineColumnsDictionary.size;

        // if stored length more than from b-end, columns were deleted
        const columnsWereDeleted = Object.keys(storedColumns).length > lineColumnsDictionary.size;

        if (columnsWereAdded) {
          // determine which column was added and its hidden
          const newColumnLettersHidden = lineColumnsDictionary.reduce((prevData, columnID, columnLetter) => {
            // if undefined - new column, and if exist in initial hidden columns - hidden
            if (storedColumns[columnID] === undefined && initialHiddenColumnLetters.includes(columnLetter)) {
              return [...prevData, columnLetter];
            }

            return prevData;
          }, []);

          // put stored columns from indexedDB with new columns to redux and indexedDb
          yield call(ensureUpdateJELineColumnsVisibility, {
            payload: [...storedHiddenColumnLetters, ...newColumnLettersHidden],
          });
        } else if (columnsWereDeleted) {
          // flip dictionary from OrderedMap<columnLetter, columnID> to OrderedMap<columnID, columnLetter>
          const lineColumnsDictionaryFlipped = lineColumnsDictionary.flip();

          const newColumnIDsHidden = Object.entries(storedColumns).reduce((prevData, [columnID, visible]) => {
            // if columnID from store exist in dictionary and is hidden - add to new hidden columns
            if (lineColumnsDictionaryFlipped.get(columnID) !== undefined && visible === false) {
              return [...prevData, columnID];
            }

            return prevData;
          }, []);

          // put stored columns from indexedDB with new columns to redux and indexedDb
          yield call(ensureUpdateJELineColumnsVisibility, {
            // convert ids to letters and I pass already flipped dictionary, and therefore I don't use third parameters
            payload: swapJEGridLineColumnsData(newColumnIDsHidden, lineColumnsDictionaryFlipped),
          });
        } else {
          // if no difference between columns state - put stored columns from indexedDB to redux
          yield put({
            type: actions.getJELineColumnsVisibilityAction.success,
            payload: storedHiddenColumnLetters,
          });
        }
      } else {
        // first time on this document or something else - we don't have stored settings for columns visibility
        // save to indexedDB and put them to redux
        yield call(ensureUpdateJELineColumnsVisibility, { payload: initialHiddenColumnLetters.toJS() });
      }
    } catch (err) {
      yield doLogout(actions.getJELineColumnsVisibilityAction, err);
    }
  }
}

// TODO: migrate to journalApi.getCellOptions (@/domain/journal/api.ts). Swapping
// the call is the easy half — this saga also writes the fetched page into the
// Redux `paginationList`, which holds a single global list plus a keyed cache
// and is deliberately preserved across JE reloads (see model.js). Moving it
// means deciding whether that state belongs in Redux at all, so it needs its
// own ticket rather than a drive-by change.
export function* ensureGetJEList({ payload }) {
  const documentID = yield select(documentDocumentIdSelector);
  const { isNewList, resolve, reject, query, include_id: includeId, preload = false } = payload;

  try {
    const {
      data: { list, pageToken },
    } = yield call(Api.getJEList, {
      params: {
        ...query,
        documentID,
      },
    });

    const optionsList = listToOptions(list);

    yield put({
      type: actions.getJEListAction.success,
      payload: {
        list: optionsList,
        pageToken,
        isNewList,
        include_id: includeId,
        preload,
      },
    });

    if (typeof resolve === 'function') {
      resolve(optionsList);
    }
  } catch (err) {
    yield doLogout(actions.getJEListAction, err);
    if (typeof reject === 'function') {
      reject();
    }
  } finally {
    if (yield cancelled()) {
      if (typeof reject === 'function') {
        reject();
      }
    }
  }
}

export function* ensureJournalEntryForGrid(
  documentID,
  reindexCache = false,
  page = 1,
  pageSize = 30,
  readonly,
  resolve,
  reject,
) {
  yield put({
    type: actions.getJournalEntryForGridAction.request,
  });

  try {
    const { data } = yield call(Api.getJournalEntry, {
      params: {
        documentID,
        reindexCache,
        page,
        pageSize,
        readonly,
      },
    });

    const payload = adapters.JEAdapter(data);

    yield put({
      type: actions.getJournalEntryForGridAction.success,
      payload,
    });

    if (typeof resolve === 'function') resolve();
  } catch (err) {
    yield put({
      type: actions.getJournalEntryForGridAction.failure,
      err,
    });
    if (typeof reject === 'function') reject();
    yield doLogout(actions.getJournalEntryForGridAction, err);
  }
  // order is important, be careful if you change it
  yield call(migrateJETablesFromLetterToID);
  yield call(migrateJETableDisabledColumnsToTableColumnsVisibility);
  yield call(ensureGetJournalSettingsPinnedCols);
  yield call(ensureJELineColumnsVisibility);
}

export function* ensureGetCachedPaginationList() {
  const cachedListIds = yield select(selectors.cachedPaginationListsIncludeIdsSelector);
  const entries = yield select(selectors.journalNotCachedPaginationListsSelector);

  const queryCreator = (cellName) => ({
    cellName,
    pageSize: QUERY_ALL_PAGES,
    pageToken: null,
    search: '',
  });

  // eslint-disable-next-line no-unused-vars,no-restricted-syntax
  for (const [i, { include_id: includeId, name }] of entries) {
    if (!cachedListIds.includes(includeId)) {
      const getCachedPaginationList = yield select(selectors.getCachedPaginationListSelector);
      const list = getCachedPaginationList(includeId);

      if (!list) {
        yield call(ensureGetJEList, {
          payload: {
            query: queryCreator(name),
            isNewList: true,
            include_id: includeId,
            preload: true,
          },
        });
        cachedListIds.push(includeId);
      }
    }
  }
}
